/**
 * Left rail of the Agent Composer. Each item is draggable onto the canvas
 * (HTML5 drag/drop handled by React Flow) and also activatable with Enter/Space
 * for keyboard accessibility.
 */
import type { ReactNode } from 'react';
import { Bot, Clock, GitBranch, Merge, Split } from 'lucide-react';
import type { ComposerNodeType } from '../../types/composer';

interface PaletteItem {
  type: ComposerNodeType;
  label: string;
  hint: string;
  color: string;
  glyph: ReactNode;
}

const ITEMS: PaletteItem[] = [
  { type: 'agent', label: 'Agent', hint: 'run an agent', color: 'var(--cx-agent)', glyph: <Bot size={16} /> },
  { type: 'condition', label: 'Condition', hint: 'true / false branch', color: 'var(--cx-condition)', glyph: <GitBranch size={16} /> },
  { type: 'wait', label: 'Wait', hint: 'pause for event / time', color: 'var(--cx-wait)', glyph: <Clock size={16} /> },
  { type: 'parallel', label: 'Parallel', hint: 'fan out', color: 'var(--cx-parallel)', glyph: <Split size={16} /> },
  { type: 'merge', label: 'Merge', hint: 'fan in', color: 'var(--cx-merge)', glyph: <Merge size={16} /> },
];

export interface ComposerPaletteProps {
  /** Keyboard fallback: add a node of the given type at a default position. */
  onAddNode: (type: ComposerNodeType) => void;
}

export const ComposerPalette = ({ onAddNode }: ComposerPaletteProps) => {
  const onDragStart = (event: React.DragEvent, type: ComposerNodeType) => {
    event.dataTransfer.setData('application/composer-node', type);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <nav className="cx-palette" aria-labelledby="palette-heading">
      <h2 id="palette-heading" className="cx-palette-heading">Blocks</h2>
      {ITEMS.map((item) => (
        <button
          key={item.type}
          type="button"
          className="cx-palette-item"
          style={{ ['--cx-type' as string]: item.color }}
          draggable
          onDragStart={(e) => onDragStart(e, item.type)}
          onClick={() => onAddNode(item.type)}
          title={`Drag onto the canvas, or activate to add a ${item.label} node`}
        >
          <span aria-hidden style={{ color: item.color, display: 'grid' }}>
            {item.glyph}
          </span>
          <span>
            {item.label}
            <span className="cx-palette-hint">{item.hint}</span>
          </span>
        </button>
      ))}
      <div className="cx-palette-help">
        Drag a block onto the canvas, or activate a button to drop one at the
        center. Use <strong>Connect</strong> in the toolbar for a keyboard-friendly
        connection dialog.
      </div>
    </nav>
  );
};
